import { Component, Input, HostListener, ElementRef, ViewChild, OnChanges } from '@angular/core';
import { ArticleListService } from './../../services/article-list.service';
import { Router } from '@angular/router';
import { ArtigosPaginados } from './../../classes/artigos-paginados';
import { MenuItem } from 'primeng/api';
import { Artigo } from './../../classes/artigo';
import { Tour } from './../../classes/tour';
import { AuxService } from '../aux.service';

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.css'],
  standalone: false,
})
export class BreadcrumbComponent implements OnChanges {
  @Input() artigo: Artigo;
  @Input() tour: Tour;
  @Input() tag: string;
  @Input() articleNotFound: boolean;

  readonly home = { label: 'Central de Ajuda', routerLink: 'home' };
  menuItems: MenuItem[] = [];
  search;
  paginatedArticles: ArtigosPaginados;
  notFound = false;
  characters = 0;
  breadcrumbReady = false;
  isDropdownOpen = false;

  constructor(
    private articleListService: ArticleListService,
    private router: Router,
    private aux: AuxService,
  ) {}

  searchValue(text) {
    this.articleListService
      .getArticleList(text, 0, 0) // Passando a página e o limite como 0, não é feito a paginação
      .subscribe((res) => {
        this.paginatedArticles = res;
        this.isDropdownOpen = true;
        if (res.artigos.length == 0) {
          this.notFound = true;
        } else {
          this.notFound = false;
        }
      });
  }

  valuechange(newValue) {
    this.characters = newValue.length;
    if (newValue.length > 2) {
      this.searchValue(newValue);
    }
  }

  @ViewChild('searchWrapper') searchWrapper: ElementRef;

  // Se clicarem fora da busca, vamos fechar os resultados
  @HostListener('document:click', ['$event'])
  clickOutside(event: MouseEvent) {
    if (
      this.searchWrapper &&
      !this.searchWrapper.nativeElement.contains(event.target)
    ) {
      this.isDropdownOpen = false;
    }
  }

  onFocusSearch() {
    if (this.search && this.search.length > 2 && this.paginatedArticles) {
      this.isDropdownOpen = true;
    }
  }

  redirectTag(tag) {
    if (tag.length != 0) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/tag'], { queryParams: { s: encodeURI(tag) } });
      });
    }
  }

  redirectArticle(article, category) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([
        '/category/' + this.aux.slugify(category) + '/article/' + article,
      ]);
    });
  }

  ngOnChanges(): void {
    if (this.isDataReady()) {
      this.buildMenuItems();
      this.breadcrumbReady = true;
    } else {
      this.breadcrumbReady = false; // Esconde enquanto dados incompletos
    }
  }

  private isDataReady(): boolean {
    if (this.tag) return true;

    if (
      this.tour &&
      this.tour.name &&
      this.tour.id &&
      this.artigo &&
      this.artigo.titulo &&
      this.artigo.resumo
    ) {
      return true;
    }

    if (
      this.artigo &&
      this.artigo.categoria &&
      this.artigo.titulo &&
      this.artigo.resumo
    ) {
      return true;
    }

    if (this.articleNotFound) return true;

    return false;
  }

  private buildMenuItems() {
    this.menuItems = [
      ...(this.tag
        ? [{ label: this.formatName(this.tag), routerLink: null }]
        : []),

      ...(!this.articleNotFound && this.tour && this.artigo && this.artigo.titulo
        ? [
            { label: this.tour.name, routerLink: null },
            { label: this.artigo.titulo, routerLink: null },
          ]
        : []),

      ...(!this.tour && !this.articleNotFound && this.artigo && this.artigo.titulo
        ? [
            {
              label: this.artigo.categoria,
              routerLink: ['/tag'],
              queryParams: { s: encodeURI(this.artigo.categoria) },
            },
            { label: this.artigo.titulo, routerLink: null },
          ]
        : []),

      ...(this.articleNotFound
        ? [{ label: 'Artigo não encontrado', routerLink: null }]
        : []),
    ];
  }

  private formatName(value: string): string {
    const lowerCaseWords = [
      'de',
      'da',
      'do',
      'das',
      'dos',
      'e',
      'em',
      'por',
      'pela',
      'pelo',
      'pelas',
      'pelos',
      'para',
      'com',
      'entre',
      'o',
      'os',
      'a',
      'as',
      'no',
      'na',
      'nos',
      'nas',
      'and',
    ];
    const clean = this.decodeFully(value.replace(/^\d+-/, ''));

    // Primeiro caractere de cada palavra em maiúsculo, exceto preposições
    return clean
      .replace(/-/g, ' ')
      .replace(/\+/g, ' ')
      .split(' ')
      .map((word, index) => {
        const lower = word.toLowerCase();
        if (index > 0 && lowerCaseWords.includes(lower)) {
          return lower;
        }
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  // Vamos decodificar mais de uma vez para evitar bugs
  private decodeFully(value: string): string {
    let decoded = value;
    for (let i = 0; i < 10; i++) {
      try {
        const temp = decodeURIComponent(decoded);
        if (temp === decoded) break;
        decoded = temp;
      } catch {
        break;
      }
    }
    return decoded;
  }
}
